import { FieldArray, Form, Formik, useField, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Client } from "@/features/clients/types";
import {
  DESIGNATION_OPTIONS,
  type FormSubmission,
  type GenerateFor,
  type PractitionerDesignation,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox, FormikSelect } from "./formik-controls";

// --- Form D-2848 (Washington DC POA) editable payload -----------------------
// snake_case keys mirror the backend `apps/forms/mappings/form_2848dc.py`
// `build_defaults` exactly (passed verbatim, no snake→camel). DC deltas vs the
// federal 2848: per-rep credential radio + required email, six free-text act
// lines, a non-taxpayer signer block, and an explicit mailing-address (apt).

/** Per-rep credential selector (Line 2 identifier). */
export type Form2848dcCredential = "dc" | "ein" | "ssn" | "ptin" | "other";

export const CREDENTIAL_OPTIONS: { value: Form2848dcCredential; label: string }[] = [
  { value: "dc", label: "DC bar no." },
  { value: "ein", label: "Firm EIN" },
  { value: "ptin", label: "PTIN" },
  { value: "ssn", label: "SSN" },
  { value: "other", label: "Other" },
];

export interface Form2848dcRepresentative {
  /** The backing Practitioner id (contact details are looked up from it server-side). */
  practitioner: string;
  active: boolean;
  name: string;
  designation: PractitionerDesignation | "";
  other_id: string;
  other_type: string;
  /** Required contact email for the representative. */
  email: string;
  credential: Form2848dcCredential;
  /** Credential identifier printed on Line 2 (bar/license no.; PTIN/EIN filled server-side). */
  rep_id: string;
}

export interface Form2848dcMatter {
  description: string;
  tax_form: string;
  years: string;
}

export interface Form2848dcData {
  generate_for: GenerateFor;
  representatives: Form2848dcRepresentative[];
  matters: Form2848dcMatter[];
  /** Six free-text "acts authorized" lines. */
  acts: string[];
  dont_revoke: boolean;
  signer_name: string;
  signer_title: string;
  signer_phone: string;
  apt: string;
}

/** The DC form fits three representatives and three tax-matter rows. */
export const MAX_DC_REPS = 3;
export const MAX_DC_MATTERS = 3;
const NUM_ACTS = 6;

const GENERATE_FOR_OPTIONS: { value: GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
];

const EMPTY_MATTER: Form2848dcMatter = { description: "", tax_form: "", years: "" };

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  representatives: Yup.array().of(
    Yup.object({
      email: Yup.string().email("Enter a valid email"),
    }),
  ),
  matters: Yup.array()
    .of(
      Yup.object({
        description: Yup.string().max(200, "Too long"),
        tax_form: Yup.string().max(40, "Too long"),
        years: yearRange,
      }),
    )
    .max(MAX_DC_MATTERS),
  acts: Yup.array().of(Yup.string().max(40, "Too long")),
});

function toInitialValues(data: Record<string, unknown>): Form2848dcData {
  const d = data as Partial<Form2848dcData>;
  const acts = Array.isArray(d.acts) ? d.acts : [];
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: d.representatives ?? [],
    matters:
      d.matters && d.matters.length > 0 ? d.matters : [{ ...EMPTY_MATTER }],
    acts: Array.from({ length: NUM_ACTS }, (_, i) => acts[i] ?? ""),
    dont_revoke: d.dont_revoke ?? false,
    signer_name: d.signer_name ?? "",
    signer_title: d.signer_title ?? "",
    signer_phone: d.signer_phone ?? "",
    apt: d.apt ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<GenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/**
 * DC representatives table — like the federal reps grid but with the DC-only
 * credential selector + required email columns; contact details come from the
 * practitioner record server-side.
 */
function DcRepresentativesTable() {
  const { values } = useFormikContext<Form2848dcData>();
  const reps = values.representatives;

  if (reps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No representatives are assigned to this client. Add them on the client's
        profile, then reopen this form.
      </p>
    );
  }

  return (
    <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Active</TableHead>
            <TableHead>Rep Name</TableHead>
            <TableHead className="w-48">Designation</TableHead>
            <TableHead className="w-36">Credential</TableHead>
            <TableHead className="w-40">Credential ID</TableHead>
            <TableHead className="w-56">Email</TableHead>
            <TableHead className="w-40">Jurisdiction</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reps.map((rep, i) => (
            <TableRow key={rep.practitioner || i}>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.active`}
                  aria-label={`Active ${rep.name}`}
                />
              </TableCell>
              <TableCell className="font-medium">{rep.name}</TableCell>
              <TableCell>
                <FormikSelect
                  name={`representatives.${i}.designation`}
                  options={DESIGNATION_OPTIONS}
                  placeholder="Select…"
                  aria-label={`Designation for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikSelect
                  name={`representatives.${i}.credential`}
                  options={CREDENTIAL_OPTIONS}
                  placeholder="Select…"
                  aria-label={`Credential type for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.rep_id`}
                  aria-label={`Credential ID for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.email`}
                  type="email"
                  aria-label={`Email for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.other_type`}
                  aria-label={`Jurisdiction for ${rep.name}`}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/** DC tax-matters table — description / form / years, capped at three rows. */
function DcMattersTable() {
  const { values } = useFormikContext<Form2848dcData>();
  const matters = values.matters;

  return (
    <FieldArray name="matters">
      {(helpers) => (
        <div className="flex flex-col gap-3">
          <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type of Tax / Matter</TableHead>
                  <TableHead className="w-36">Tax Form Number</TableHead>
                  <TableHead className="w-44">Year(s) or Period(s)</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((_matter, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.description`}
                        placeholder="e.g. Income Tax"
                        aria-label={`Type of tax for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_form`}
                        placeholder="e.g. D-40"
                        aria-label={`Tax form for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.years`}
                        placeholder="e.g. 2021, 2022"
                        aria-label={`Years for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove matter ${i + 1}`}
                        disabled={matters.length === 1}
                        onClick={() => helpers.remove(i)}
                      >
                        <Trash2 className="text-muted-foreground size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={matters.length >= MAX_DC_MATTERS}
              onClick={() => helpers.push({ ...EMPTY_MATTER })}
            >
              <Plus /> Add tax matter
            </Button>
            {matters.length >= MAX_DC_MATTERS && (
              <span className="text-muted-foreground ml-3 text-xs">
                Form D-2848 fits up to {MAX_DC_MATTERS} matters.
              </span>
            )}
          </div>
        </div>
      )}
    </FieldArray>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form D-2848 (Washington DC Power of Attorney) — prefilled from the
 * client + its practitioners. DC-specific: each rep has a credential selector +
 * email, "acts authorized" is six free-text lines, and there is a non-taxpayer
 * signer block plus an explicit mailing address.
 */
export function Form2848dcEditor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
            <div className="grid max-w-xs gap-2">
              <Label htmlFor="apt">Apt / Suite no.</Label>
              <FormikCell name="apt" placeholder="Optional" />
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Power of Attorney Given To</SectionHeading>
          <DcRepresentativesTable />
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeading>Tax Matters</SectionHeading>
          <DcMattersTable />
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Acts Authorized</SectionHeading>
          <div className="grid gap-3">
            {Array.from({ length: NUM_ACTS }, (_, i) => (
              <FormikCell
                key={i}
                name={`acts.${i}`}
                maxLength={40}
                placeholder={`Authorized act ${i + 1}`}
                aria-label={`Authorized act ${i + 1}`}
              />
            ))}
          </div>
          <FormikCheckbox
            name="dont_revoke"
            label="Do not revoke prior Powers of Attorney"
          />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Signer (if not the taxpayer)</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="signer_name">Name</Label>
              <FormikCell name="signer_name" aria-label="Signer name" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="signer_title">Title</Label>
              <FormikCell name="signer_title" aria-label="Signer title" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="signer_phone">Phone</Label>
              <FormikCell name="signer_phone" aria-label="Signer phone" />
            </div>
          </div>
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
